import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Coffee,
  Heart,
  Lightning,
  Users,
  ArrowRight,
} from "@phosphor-icons/react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";

// About page. Witty + sarcastic tone. Tells the why-this-exists story.
export default function About() {
  return (
    <div className="min-h-screen tg-public-bg">
      <PublicNav />

      {/* HERO */}
      <section className="mx-auto max-w-[1600px] px-4 pt-16 pb-12 md:pt-24 md:pb-16">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 mb-6 tg-shadow-soft">
            <span className="h-1.5 w-1.5 rounded-full tg-gradient" />
            About the team
          </span>
          <h1 className="text-5xl md:text-7xl tracking-tight leading-[1.05] text-slate-950">
            We believe in the <span className="tg-gradient-text">power of caffeine,</span><br />
            questionable snacks, and{" "}
            <span className="tg-gradient-text">spreadsheets done right.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Come, bear witness to our breath-taking accomplishments. Mostly
            arguing about whether "due date" and "decision date" should be
            different columns. (Spoiler: yes. Obviously.)
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="mx-auto max-w-[1600px] px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Stat number="600+" label="Hours of R&D and sleeplessness" tone="violet" />
          <Stat number="15" label="Versions before we got a stable one" tone="indigo" />
          <Stat number="2.5×" label="Faster than the fastest infant" tone="cyan" />
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white border-y border-slate-200 py-20">
        <div className="mx-auto max-w-[1600px] px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3 font-sf-label">
              What we care about
            </div>
            <h2 className="text-4xl md:text-5xl text-slate-950">
              About <span className="tg-gradient-text">ThatGantt</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Here's just some of the little things that motivated us to make
              this killer app. Nothing shy of blood, sweat, and tears. We're
              practically Avengers — if the Avengers had a recurring Tuesday
              standup and one of them was named Brad from Finance.
            </p>
          </div>

          <div className="space-y-5">
            <Value
              icon={Heart}
              tone="violet"
              title="Passion"
              copy="For icons, swim lane diagrams, and the ole' having technical difficulties with my comp so no cam today..."
            />
            <Value
              icon={Lightning}
              tone="indigo"
              title="Empowerment"
              copy="To the 'No-Cam' legends who haven't put on pants since the 2022 lockdown kickoff. But honestly, designing a clean chart in under 2 minutes should be a pants optional kinda thing."
            />
            <Value
              icon={Users}
              tone="cyan"
              title="Community"
              copy="Because, after all, we plan things to ship them to other people. Sharing should be one click. Permissions should be obvious. PMO can sleep at night."
            />
          </div>
        </div>
      </section>

      {/* HIDDEN COSTS — modeled after the trip-tree "hidden costs" pattern */}
      <section className="py-24">
        <div className="mx-auto max-w-[1600px] px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <h2 className="text-4xl md:text-5xl text-slate-950 mb-5">
              The hidden costs <span className="tg-gradient-text">of bad PM tools.</span>
            </h2>
            <p className="text-slate-600 leading-relaxed">
              During years of running projects in tools that pretended a task
              has one date, we uncovered why project management feels like a
              <strong className="text-slate-950"> never-ending budget mystery hour</strong>{" "}
              for most teams. Three culprits — every single time.
            </p>
            <div className="mt-8 rounded-2xl tg-gradient-soft border border-indigo-100 p-6">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">
                The 3 Big Issues
              </div>
              <div className="text-2xl md:text-3xl text-slate-950">
                Cookie-cutter views.<br />
                Imprecise progress.<br />
                Overwhelming options.
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <Issue
              num="01"
              title="Impersonal, cookie-cutter charts"
              copy="Existing tools fail to recognize that PMs — even tracking the same delivery — have unique workflows. That's why our custom columns, dependencies, and saved views are key. They make the chart fit your project, not the other way around."
              accent
            />
            <Issue
              num="02"
              title="Big budgets, imprecise dates"
              copy="Did you know almost 60% of project overruns come from tracking a single date per task? You overestimate progress because tools can't distinguish 'designed' from 'shipped.' ThatGantt makes you log both. We're annoying like that. You're welcome."
            />
            <Issue
              num="03"
              title="Overwhelming options"
              copy="Launched a quick project recently? There's like a million tools, integrations, and 'AI co-pilots.' How do you make sense of it all so you don't waste the little time-off you have? You don't. You use ThatGantt and call it a day."
            />
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1600px] px-4">
          <div className="rounded-3xl tg-gradient p-10 md:p-16 text-white tg-shadow-soft text-center">
            <h2 className="text-4xl md:text-5xl mb-4 text-white">
              Stop tracking projects in a spreadsheet.
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8 leading-relaxed">
              Free tier covers 10 projects. No card required. Cancel anytime —
              we built the export button first, on purpose.
            </p>
            <Link to="/register">
              <Button
                className="h-12 px-7 rounded-full bg-white text-indigo-700 hover:bg-slate-100 font-medium text-base gap-2 border-0"
                data-testid="about-cta-register"
              >
                Start free
                <ArrowRight size={16} weight="bold" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
      <HaveAQuestionBadge />
    </div>
  );
}

// ---------- helpers ----------

function Stat({ number, label, tone }) {
  const toneMap = {
    violet: "from-violet-400 to-indigo-500",
    indigo: "from-indigo-400 to-blue-500",
    cyan: "from-blue-400 to-cyan-500",
  };
  return (
    <div
      className={`rounded-2xl bg-gradient-to-br ${toneMap[tone]} p-8 text-white tg-shadow-soft`}
      data-testid={`about-stat-${label.toLowerCase().replace(/\s+/g, "-").slice(0, 20)}`}
    >
      <div className="text-5xl md:text-6xl font-display font-bold tracking-tight">{number}</div>
      <div className="mt-2 text-sm text-white/90">{label}</div>
    </div>
  );
}

function Value({ icon: Icon, tone, title, copy }) {
  const toneMap = {
    violet: "from-violet-400 to-indigo-500",
    indigo: "from-indigo-400 to-blue-500",
    cyan: "from-blue-400 to-cyan-500",
  };
  return (
    <div className="flex items-start gap-4">
      <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${toneMap[tone]} flex items-center justify-center text-white shrink-0`}>
        <Icon size={18} weight="fill" />
      </div>
      <div>
        <div className="text-xl font-semibold text-slate-950">{title}</div>
        <div className="mt-1 text-sm text-slate-600 leading-relaxed">{copy}</div>
      </div>
    </div>
  );
}

function Issue({ num, title, copy, accent }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${accent ? "border-indigo-200 bg-white tg-shadow-soft" : "border-slate-200 bg-white"}`}
      data-testid={`about-issue-${num}`}
    >
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">{num}</span>
        <div className="text-lg font-semibold text-slate-950">{title}</div>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{copy}</p>
    </div>
  );
}
